/**
 * Browser implementation of AudioTrainer.
 *
 * Architecture: a small CNN trained directly on browser-FFT spectrograms.
 * No external speech model — the spectrogram captured by the microphone
 * adapter (43 frames × 232 bins) is dense enough that a 2-conv CNN with
 * a dense head trains in seconds and runs at >10Hz on a Chromebook.
 *
 * Real-browser-only — exercised end-to-end in Playwright. Pure helpers
 * (`audioFeatures.ts`, `checkAudioTrainability`) are unit-tested; the
 * TF.js bits live here and are excluded from coverage like
 * `browserTrainer.ts`.
 */

import type { Class, Sample, TrainedLabel } from '../data/types';
import { SPECTROGRAM_BINS, SPECTROGRAM_FRAMES } from '../data/microphone';
import {
  decodeWavBlob,
  normalizeSpectrogram,
  pcmToSpectrogram,
  TARGET_SAMPLE_RATE,
} from './audioFeatures';
import type { AudioTrainer, AudioTrainInput, LoadedAudioModel } from './audioTrainer';
import { extractTfjsLayersModelArtifacts } from './extractArtifacts';
import {
  buildExportFilename,
  checkConvergence,
  clampHyperparams,
  TrainingInputError,
  type ExportModelInput,
  type ModelArtifacts,
  type TrainResult,
} from './trainer';

import { getTf } from './tfBackend';

type Tf = typeof import('@tensorflow/tfjs');

function modelKey(projectId: string): string {
  return `indexeddb://bryta-audio-${projectId}`;
}

/** The audio default epochs override the trainer's 20 — small CNN
 *  spectrogram models converge a touch slower than the image head. */
const AUDIO_DEFAULT_EPOCHS = 25;
const HEAD_HIDDEN_UNITS = 64;

function bucketByClass(input: AudioTrainInput): Map<string, Sample[]> {
  const buckets = new Map<string, Sample[]>();
  for (const sample of input.samples) {
    const bucket = buckets.get(sample.classId);
    if (bucket) bucket.push(sample);
    else buckets.set(sample.classId, [sample]);
  }
  return buckets;
}

function buildLabels(classes: Class[], buckets: Map<string, Sample[]>): TrainedLabel[] {
  return classes
    .filter((cls) => (buckets.get(cls.id)?.length ?? 0) > 0)
    .map((cls) => ({ classId: cls.id, name: cls.name }));
}

export const browserAudioTrainer: AudioTrainer = {
  async train(input) {
    const { signal } = input;
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    // The audio default epoch count is higher than the image trainer's
    // — clamp against the shared bounds, but use 25 as the fallback.
    const params = clampHyperparams({
      epochs: AUDIO_DEFAULT_EPOCHS,
      ...input.hyperparams,
    });

    const tf = await getTf();
    input.onProgress?.({ phase: 'preparing' });

    const buckets = bucketByClass(input);
    const labels = buildLabels(input.classes, buckets);
    if (labels.length < 2) {
      throw new TrainingInputError('Need at least two classes with clips to train.');
    }

    const totalSamples = input.samples.length;
    const xs: Float32Array[] = [];
    const ys: number[] = [];
    let processed = 0;

    for (let labelIndex = 0; labelIndex < labels.length; labelIndex++) {
      const label = labels[labelIndex]!;
      const samples = buckets.get(label.classId) ?? [];
      for (const sample of samples) {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        const { pcm, sampleRate } = await decodeWavBlob(sample.blob);
        // Spectrograms are computed against the canonical sample rate so
        // a model trained on one device classifies the same input the
        // same way on another. The MicPanel resamples on capture, so
        // sampleRate should already be TARGET_SAMPLE_RATE — but we still
        // pass it through pcmToSpectrogram so a kid who restored a
        // project file from a different recorder gets a sane frame rate.
        const spec = pcmToSpectrogram(pcm, sampleRate || TARGET_SAMPLE_RATE);
        xs.push(normalizeSpectrogram(spec));
        ys.push(labelIndex);
        processed++;
        input.onProgress?.({ phase: 'extracting', current: processed, total: totalSamples });
      }
    }

    if (xs.length === 0) {
      throw new TrainingInputError('No usable audio clips were found.');
    }

    const featureSize = xs[0]!.length;
    const xData = new Float32Array(xs.length * featureSize);
    for (let i = 0; i < xs.length; i++) xData.set(xs[i]!, i * featureSize);

    const xTensor = tf.tensor4d(xData, [xs.length, SPECTROGRAM_FRAMES, SPECTROGRAM_BINS, 1]);
    const yTensor = tf.oneHot(tf.tensor1d(ys, 'int32'), labels.length);

    const model = tf.sequential();
    model.add(
      tf.layers.conv2d({
        inputShape: [SPECTROGRAM_FRAMES, SPECTROGRAM_BINS, 1],
        filters: 8,
        kernelSize: [3, 3],
        activation: 'relu',
        padding: 'same',
      }),
    );
    model.add(tf.layers.maxPooling2d({ poolSize: [2, 2] }));
    model.add(
      tf.layers.conv2d({
        filters: 16,
        kernelSize: [3, 3],
        activation: 'relu',
        padding: 'same',
      }),
    );
    model.add(tf.layers.maxPooling2d({ poolSize: [2, 2] }));
    model.add(tf.layers.flatten());
    model.add(tf.layers.dense({ units: HEAD_HIDDEN_UNITS, activation: 'relu' }));
    model.add(tf.layers.dropout({ rate: 0.4 }));
    model.add(tf.layers.dense({ units: labels.length, activation: 'softmax' }));
    model.compile({
      optimizer: tf.train.adam(params.learningRate),
      loss: 'categoricalCrossentropy',
      metrics: ['accuracy'],
    });

    let aborted = false;
    const onAbort = (): void => {
      aborted = true;
      model.stopTraining = true;
    };
    signal?.addEventListener('abort', onAbort);

    const lossHistory: number[] = [];
    let lastAccuracy = 0;

    try {
      await model.fit(xTensor, yTensor, {
        epochs: params.epochs,
        batchSize: params.batchSize,
        shuffle: true,
        callbacks: {
          onEpochEnd: (epoch, logs) => {
            const loss = logs?.loss ?? 0;
            const accuracy = logs?.acc ?? logs?.accuracy ?? 0;
            lossHistory.push(loss);
            lastAccuracy = accuracy;
            input.onProgress?.({
              phase: 'training',
              epoch: epoch + 1,
              epochs: params.epochs,
              loss,
              accuracy,
            });
          },
        },
      });
      if (aborted) throw new DOMException('Aborted', 'AbortError');
      await model.save(modelKey(input.projectId));
    } finally {
      signal?.removeEventListener('abort', onAbort);
      xTensor.dispose();
      yTensor.dispose();
      model.dispose();
    }

    const sampleCounts: Record<string, number> = {};
    for (const label of labels) {
      sampleCounts[label.classId] = buckets.get(label.classId)?.length ?? 0;
    }

    const finalLoss = lossHistory[lossHistory.length - 1] ?? 0;
    const { converged } = checkConvergence(lossHistory);

    return {
      labels,
      sampleCounts,
      lossHistory,
      finalLoss,
      finalAccuracy: lastAccuracy,
      converged,
    } satisfies TrainResult;
  },

  async removeModel(projectId) {
    try {
      const tf = await getTf();
      await tf.io.removeModel(modelKey(projectId));
    } catch (err) {
      if (err instanceof Error && /not.*find/i.test(err.message)) return;
      console.warn('audio removeModel failed:', err);
    }
  },

  async loadModel(projectId) {
    const tf = await getTf();
    let head: Awaited<ReturnType<Tf['loadLayersModel']>>;
    try {
      head = await tf.loadLayersModel(modelKey(projectId));
    } catch {
      return null;
    }
    return { head, tf } as LoadedAudioModel;
  },

  async predictAudio(loaded, pcm, sampleRate) {
    const { head, tf } = loaded as {
      head: Awaited<ReturnType<Tf['loadLayersModel']>>;
      tf: Tf;
    };
    // CRITICAL: must mirror the training pipeline exactly. Training reads
    // a stored WAV blob → decodeWavBlob → pcmToSpectrogram(pcm, sampleRate)
    // → normalize. Live prediction now does the same: live PCM →
    // pcmToSpectrogram → normalize. A previous version computed
    // spectrograms via AnalyserNode at predict time, which produced a
    // different value distribution and made the CNN collapse to "always
    // predict the majority class".
    const spec = pcmToSpectrogram(pcm, sampleRate || TARGET_SAMPLE_RATE);
    const normalized = normalizeSpectrogram(spec);
    const probs = tf.tidy(() => {
      const tensor = tf.tensor4d(normalized, [1, SPECTROGRAM_FRAMES, SPECTROGRAM_BINS, 1]);
      return head.predict(tensor) as ReturnType<Tf['tensor']>;
    });
    const data = await probs.data();
    probs.dispose();
    return Array.from(data as Float32Array);
  },

  disposeModel(loaded) {
    const { head } = loaded as { head: { dispose(): void } };
    try {
      head.dispose();
    } catch {
      // Already disposed; no-op.
    }
  },

  async extractArtifacts(input: ExportModelInput): Promise<ModelArtifacts> {
    return extractTfjsLayersModelArtifacts({
      modelKey: modelKey(input.projectId),
      input,
      extras: {
        brytaProjectKind: 'audio',
        brytaModelKind: 'audio',
        brytaSpectrogramFrames: SPECTROGRAM_FRAMES,
        brytaSpectrogramBins: SPECTROGRAM_BINS,
        brytaTargetSampleRate: TARGET_SAMPLE_RATE,
      },
    });
  },

  async exportModel(input) {
    const tf = await getTf();
    const { zipSync } = await import('fflate');
    let head: Awaited<ReturnType<Tf['loadLayersModel']>>;
    try {
      head = await tf.loadLayersModel(modelKey(input.projectId));
    } catch {
      throw new Error('No trained model was found to export.');
    }
    try {
      head.setUserDefinedMetadata({
        brytaProjectName: input.projectName,
        brytaLabels: input.labels,
        brytaProjectKind: 'audio',
        brytaSpectrogramFrames: SPECTROGRAM_FRAMES,
        brytaSpectrogramBins: SPECTROGRAM_BINS,
        brytaSchemaVersion: 1,
      });

      let captured: import('@tensorflow/tfjs').io.ModelArtifacts | null = null;
      const saveHandler: import('@tensorflow/tfjs').io.IOHandler = {
        save: async (artifacts) => {
          captured = artifacts;
          let weightDataBytes = 0;
          if (artifacts.weightData) {
            if (Array.isArray(artifacts.weightData)) {
              for (const buf of artifacts.weightData) weightDataBytes += buf.byteLength;
            } else {
              weightDataBytes = artifacts.weightData.byteLength;
            }
          }
          return {
            modelArtifactsInfo: {
              dateSaved: new Date(),
              modelTopologyType: 'JSON',
              modelTopologyBytes:
                typeof artifacts.modelTopology === 'string'
                  ? artifacts.modelTopology.length
                  : JSON.stringify(artifacts.modelTopology ?? {}).length,
              weightDataBytes,
              weightSpecsBytes: JSON.stringify(artifacts.weightSpecs ?? []).length,
            },
          };
        },
      };
      await head.save(saveHandler);
      if (!captured) throw new Error('TF.js did not produce model artifacts.');
      const artifacts = captured as import('@tensorflow/tfjs').io.ModelArtifacts;

      const modelJson = {
        modelTopology: artifacts.modelTopology,
        format: artifacts.format ?? 'layers-model',
        generatedBy: 'bryta-learn',
        convertedBy: null,
        weightsManifest: [{ paths: ['./weights.bin'], weights: artifacts.weightSpecs ?? [] }],
        userDefinedMetadata: artifacts.userDefinedMetadata ?? {
          brytaProjectName: input.projectName,
          brytaLabels: input.labels,
          brytaProjectKind: 'audio',
          brytaSchemaVersion: 1,
        },
      };
      const metadataJson = {
        brytaProjectName: input.projectName,
        brytaLabels: input.labels,
        brytaProjectKind: 'audio',
        brytaSpectrogramFrames: SPECTROGRAM_FRAMES,
        brytaSpectrogramBins: SPECTROGRAM_BINS,
        brytaSchemaVersion: 1,
        exportedAt: new Date().toISOString(),
      };

      const enc = (s: string): Uint8Array => new Uint8Array(new TextEncoder().encode(s));
      const weightBuffers: ArrayBuffer[] = [];
      if (artifacts.weightData) {
        if (Array.isArray(artifacts.weightData)) {
          for (const buf of artifacts.weightData) weightBuffers.push(buf);
        } else {
          weightBuffers.push(artifacts.weightData);
        }
      }
      const totalBytes = weightBuffers.reduce((sum, b) => sum + b.byteLength, 0);
      const weightData = new Uint8Array(totalBytes);
      let offset = 0;
      for (const buf of weightBuffers) {
        weightData.set(new Uint8Array(buf), offset);
        offset += buf.byteLength;
      }

      const zipped = zipSync({
        'model.json': enc(JSON.stringify(modelJson, null, 2)),
        'weights.bin': weightData,
        'metadata.json': enc(JSON.stringify(metadataJson, null, 2)),
      });

      const filename = buildExportFilename(input.projectName);
      const copy = new Uint8Array(zipped.byteLength);
      copy.set(zipped);
      const blob = new Blob([copy], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${filename}.zip`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } finally {
      head.dispose();
    }
  },
};
